import type { StepConfig, Section, Card, FormValues } from '../types';
import { getStepFields } from './steps';
import { getAddMoreConfig } from './addMoreConfig';
import { getAllFieldIdsFromCondition } from './conditions';

/**
 * Finds which section/card a step belongs to
 */
const SECTION_LIST_STEP_ID = 'step-3-sections';

export function findSectionForStep(
  stepId: string,
  allSteps: StepConfig[]
): {
  sectionId: string;
  section: Section;
  cardId?: string;
  card?: Card;
  stepIndex: number;
} | null {
  let resultFromSectionListStep: ReturnType<typeof findSectionForStep> = null;

  for (const step of allSteps) {
    if (step.sections) {
      for (const section of step.sections) {
        if (section.cards) {
          for (const card of section.cards) {
            if (card.stepIds && card.stepIds.includes(stepId)) {
              const stepIndex = card.stepIds.indexOf(stepId);
              const match = { sectionId: section.id, section, cardId: card.id, card, stepIndex };
              if (step.id === SECTION_LIST_STEP_ID) {
                return match; // Prefer section list step's definition
              }
              if (!resultFromSectionListStep) {
                resultFromSectionListStep = match;
              }
            }
          }
        } else if (section.stepIds && section.stepIds.includes(stepId)) {
          const stepIndex = section.stepIds.indexOf(stepId);
          const match = { sectionId: section.id, section, stepIndex };
          if (step.id === SECTION_LIST_STEP_ID) {
            return match;
          }
          if (!resultFromSectionListStep) {
            resultFromSectionListStep = match;
          }
        }
      }
    }
  }

  return resultFromSectionListStep;
}

/**
 * Finds a section by its ID
 */
export function findSectionById(sectionId: string, allSteps: StepConfig[]): Section | null {
  for (const step of allSteps) {
    if (step.sections) {
      const foundSection = step.sections.find((sec) => sec.id === sectionId);
      if (foundSection) {
        return foundSection;
      }
    }
  }
  return null;
}

/**
 * Finds the next card in a section after a given card
 */
export function findNextCardInSection(section: Section, currentCardId: string): Card | null {
  if (!section.cards) return null;
  const currentCardIndex = section.cards.findIndex((card) => card.id === currentCardId);
  if (currentCardIndex === -1 || currentCardIndex >= section.cards.length - 1) {
    return null;
  }
  return section.cards[currentCardIndex + 1];
}

/**
 * Finds a card by its ID (searches all sections)
 */
export function findCardByIdGlobal(
  cardId: string,
  allSteps: StepConfig[]
): {
  card: Card;
  section: Section;
  sectionId: string;
} | null {
  for (const step of allSteps) {
    if (step.sections) {
      for (const section of step.sections) {
        if (section.cards) {
          const card = section.cards.find((c) => c.id === cardId);
          if (card) {
            return { card, section, sectionId: section.id };
          }
        }
      }
    }
  }
  return null;
}

/**
 * Collects form data from all steps in a card
 */
export function collectCardFormData(
  card: Card,
  stepsById: Record<string, StepConfig>,
  values: FormValues
): FormValues {
  const allFormData: FormValues = {};
  card.stepIds.forEach((stepId) => {
    const cardStep = stepsById[stepId];
    if (cardStep) {
      const cardStepFields = getStepFields(cardStep);
      cardStepFields.forEach((field) => {
        // Skip if field is in excludeFieldIds
        if (card.excludeFieldIds && card.excludeFieldIds.includes(field.id)) {
          return;
        }
        // Only include if includeFieldIds is not specified, or field is in includeFieldIds
        if (!card.includeFieldIds || card.includeFieldIds.includes(field.id)) {
          if (values[field.id] !== undefined) {
            allFormData[field.id] = values[field.id];
          }
        }
      });
    }
  });
  addShortTextValuesForFields(card.stepIds, stepsById, values, allFormData);
  return allFormData;
}

/**
 * Collects form data from all steps in a section (legacy support)
 */
export function collectSectionFormData(
  section: Section,
  stepsById: Record<string, StepConfig>,
  values: FormValues
): FormValues {
  const allFormData: FormValues = {};
  if (section.stepIds) {
    section.stepIds.forEach((stepId) => {
      const sectionStep = stepsById[stepId];
      if (sectionStep) {
        const sectionStepFields = getStepFields(sectionStep);
        sectionStepFields.forEach((field) => {
          if (values[field.id] !== undefined) {
            allFormData[field.id] = values[field.id];
          }
        });
      }
    });
  }
  if (section.stepIds) {
    addShortTextValuesForFields(section.stepIds, stepsById, values, allFormData);
  }
  return allFormData;
}

function addShortTextValuesForFields(
  stepIds: string[],
  stepsById: Record<string, StepConfig>,
  values: FormValues,
  allFormData: FormValues
): void {
  stepIds.forEach((stepId) => {
    const step = stepsById[stepId];
    if (!step) return;
    const fields = getStepFields(step);
    fields.forEach((field) => {
      if (!field.options || values[field.id] === undefined) return;
      const option = field.options.find((opt) => opt.value === String(values[field.id]));
      const shortText = (option as { shortText?: string; label?: string; value?: string })
        ?.shortText;
      if (shortText) {
        allFormData[`${field.id}ShortText`] = shortText;
      }
    });
  });
}

/**
 * Clears form values for all fields in a card
 */
export function clearCardFormData(
  card: Card,
  stepsById: Record<string, StepConfig>,
  values: FormValues
): FormValues {
  const clearedValues = { ...values };
  const preserveFieldIds = new Set<string>();

  // Preserve addMore gate fields (e.g. hasCharities/hasCashGiftToSomeone) so
  // radio selections survive card transitions even when no instances are saved yet.
  card.stepIds.forEach((stepId) => {
    const cardStep = stepsById[stepId];
    if (!cardStep) return;
    const addMoreConfig = getAddMoreConfig(cardStep);
    if (!addMoreConfig?.enableCondition) return;
    getAllFieldIdsFromCondition(addMoreConfig.enableCondition).forEach((fieldId) =>
      preserveFieldIds.add(fieldId)
    );
  });

  card.stepIds.forEach((stepId) => {
    const cardStep = stepsById[stepId];
    if (cardStep) {
      const stepFields = getStepFields(cardStep);
      stepFields.forEach((field) => {
        if (preserveFieldIds.has(field.id)) return;
        delete clearedValues[field.id];
      });
    }
  });
  return clearedValues;
}

/**
 * Clears form values for all fields in a section (legacy support)
 */
export function clearSectionFormData(
  section: Section,
  stepsById: Record<string, StepConfig>,
  values: FormValues
): FormValues {
  const clearedValues = { ...values };
  if (section.stepIds) {
    section.stepIds.forEach((stepId) => {
      const sectionStep = stepsById[stepId];
      if (sectionStep) {
        const stepFields = getStepFields(sectionStep);
        stepFields.forEach((field) => {
          delete clearedValues[field.id];
        });
      }
    });
  }
  return clearedValues;
}

export function findCardByGroupKey(
  groupKey: string,
  allSteps: StepConfig[]
): {
  card: Card;
  section: Section;
  sectionId: string;
} | null {
  for (const step of allSteps) {
    if (step.sections) {
      for (const section of step.sections) {
        if (section.cards) {
          const card = section.cards.find((c) => c.groupKey === groupKey);
          if (card) {
            return { card, section, sectionId: section.id };
          }
        }
      }
    }
  }
  return null;
}
